import { Link } from "react-router-dom";
import { ArrowRight, Clock, Tag } from "lucide-react";
import Header from "@/components/Header";
import TopAlertBar from "@/components/TopAlertBar";
import Footer from "@/components/Footer";
import FloatingCallButton from "@/components/FloatingCallButton";
import WhatsAppButton from "@/components/WhatsAppButton";
import { ZoneProvider } from "@/lib/ZoneContext";
import { DEFAULT_ZONE } from "@/lib/zones";
import { BLOG_POSTS } from "@/lib/blogPosts";
import useScrollReveal from "@/lib/useScrollReveal";

export default function BlogListPage() {
  useScrollReveal();
  const featured = BLOG_POSTS[0];
  const rest = BLOG_POSTS.slice(1);

  return (
    <ZoneProvider zone={DEFAULT_ZONE}>
      <div className="min-h-screen bg-white">
        <TopAlertBar />
        <Header />

        <main>
          {/* Hero blog */}
          <section className="relative bg-[#0A0A0A] text-white py-20 sm:py-28 lg:py-32 overflow-hidden">
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage:
                  "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
                backgroundSize: "60px 60px",
              }}
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-500 mb-4">
                — Le Blog SOS Serrurier
              </div>
              <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-7xl uppercase leading-[0.95] max-w-4xl">
                Conseils, prix et bons<br />réflexes serrurerie
              </h1>
              <p className="mt-6 text-lg text-slate-300 max-w-2xl">
                Tout ce qu'il faut savoir avant d'appeler un serrurier : tarifs honnêtes,
                arnaques à éviter, et solutions concrètes face à une porte claquée ou une
                serrure bloquée.
              </p>
            </div>
          </section>

          {/* Featured */}
          <section className="bg-white py-16 sm:py-20 reveal" data-testid="blog-featured">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Link
                to={`/blog/${featured.slug}`}
                data-testid={`blog-card-${featured.slug}`}
                className="group grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center"
              >
                <div className="lg:col-span-7 relative aspect-[16/10] overflow-hidden bg-slate-100 border border-black/10">
                  <img
                    src={featured.cover}
                    alt={featured.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em]">
                    À la une
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] font-bold text-red-600 mb-4">
                    <Tag className="w-3.5 h-3.5" />
                    {featured.category}
                    <span className="text-slate-400">·</span>
                    <Clock className="w-3.5 h-3.5" />
                    {featured.readMin} min
                  </div>
                  <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl uppercase leading-[0.95] text-[#0A0A0A] group-hover:text-red-600 transition">
                    {featured.title}
                  </h2>
                  <p className="mt-5 text-base sm:text-lg text-slate-600">
                    {featured.excerpt}
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-red-600 group-hover:gap-3 transition-all">
                    Lire l'article
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </Link>
            </div>
          </section>

          {/* Grid */}
          <section className="bg-[#F3F4F6] py-16 sm:py-20 reveal">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-xs tracking-[0.25em] uppercase font-bold text-red-600 mb-4">
                — Tous les articles
              </div>
              <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl uppercase leading-[0.95] text-[#0A0A0A] mb-12">
                À lire aussi
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    data-testid={`blog-card-${post.slug}`}
                    className="group bg-white border border-black/10 overflow-hidden hover:-translate-y-1 transition-all hover:shadow-xl"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                      <img
                        src={post.cover}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] font-bold text-red-600 mb-3">
                        {post.category}
                        <span className="text-slate-400">·</span>
                        <Clock className="w-3 h-3" />
                        {post.readMin} min
                      </div>
                      <h3 className="font-display font-black tracking-tight text-xl sm:text-2xl uppercase leading-tight text-[#0A0A0A] group-hover:text-red-600 transition">
                        {post.title}
                      </h3>
                      <p className="mt-3 text-sm text-slate-600 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-red-600 group-hover:gap-2.5 transition-all">
                        Lire
                        <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingCallButton />
        <WhatsAppButton />
      </div>
    </ZoneProvider>
  );
}
